import { useState } from "react";
import { X, Copy } from "lucide-react";

export default function CopyDescriptionDialog({ sourceUnit, allUnits, onClose, onCopy }) {
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const initialTargets = allUnits.filter((u) => u.id !== sourceUnit.id).map((u) => u.id);
  const [selected, setSelected] = useState(new Set(initialTargets));

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(initialTargets));
  const selectNone = () => setSelected(new Set());

  const candidates = allUnits.filter((u) => u.id !== sourceUnit.id);
  const wouldChange = candidates.filter((u) => {
    if (!selected.has(u.id)) return false;
    if (onlyEmpty && (u.description || "").trim()) return false;
    return true;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="copy-description-dialog">
      <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold">Kopioi työnkuva</div>
            <h3 className="font-display text-lg font-semibold text-slate-900">{sourceUnit.label}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="copy-dialog-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Kopioitava työnkuva</div>
            <div className="text-sm text-slate-800">{sourceUnit.description || "(tyhjä)"}</div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyEmpty}
              onChange={(e) => setOnlyEmpty(e.target.checked)}
              className="h-4 w-4"
              data-testid="copy-only-empty"
            />
            Vain tyhjiin työnkuviin (älä korvaa olemassaolevia)
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700">Yksiköt</div>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} className="text-emerald-700 font-semibold" data-testid="copy-select-all">Valitse kaikki</button>
                <span className="text-slate-300">·</span>
                <button onClick={selectNone} className="text-slate-500 font-semibold" data-testid="copy-select-none">Tyhjennä</button>
              </div>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {candidates.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                  data-testid={`copy-target-${u.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{u.label}</div>
                    <div className="text-xs text-slate-500 truncate">{u.description || "Ei työnkuvaa"}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {wouldChange.length} yksikkö{wouldChange.length !== 1 ? "ä" : ""} päivitetään
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost" data-testid="copy-cancel">
              Peruuta
            </button>
            <button
              onClick={() => onCopy(sourceUnit.description || "", Array.from(selected), onlyEmpty)}
              disabled={wouldChange.length === 0}
              className="btn-primary disabled:opacity-50"
              data-testid="copy-confirm"
            >
              <Copy className="h-4 w-4" /> Kopioi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
